import type { InitOptions } from 'i18next';

import i18next from 'i18next';
import { getStorage } from 'minimal-shared/utils';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next, I18nextProvider as Provider } from 'react-i18next';
import type { LangCode } from './locales-config';

import { i18nOptions, fallbackLng, storageConfig } from './locales-config';

// ----------------------------------------------------------------------

const i18nextLng = getStorage(
  storageConfig.localStorage.key,
  storageConfig.localStorage.autoDetection ? undefined : fallbackLng,
) as LangCode;

/**
 * Initialize i18next
 */
const initOptions: InitOptions = {
  ...i18nOptions(i18nextLng),
  detection: { caches: ['localStorage'] },
};

i18next.use(LanguageDetector).use(initReactI18next).init(initOptions);

// Keep <html lang> in sync with the active language. Without this the document
// advertises the wrong language and browsers offer/auto-run translation — which
// mistranslates Italian content that is also valid English (e.g. a selection
// named "Estate" became "Proprietà"). Subscribing to `languageChanged` keeps it
// correct through any future in-app language switching too.
if (typeof document !== 'undefined') {
  document.documentElement.lang = i18next.resolvedLanguage ?? fallbackLng;
  i18next.on('languageChanged', (lng) => {
    document.documentElement.lang = lng;
  });
}

// ----------------------------------------------------------------------

type I18nProviderProps = {
  children: React.ReactNode;
};

export function I18nProvider({ children }: I18nProviderProps) {
  return <Provider i18n={i18next}>{children}</Provider>;
}
